import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  FRCWidgetCompleteEvent,
  FRCWidgetWidgetErrorEvent,
  WidgetHandle,
  WidgetState,
} from '@friendlycaptcha/sdk';

import { generateFriendlyCaptchaInstance } from './module-instance';

const FriendlyCaptcha = React.memo(
  ({
    onSolved,
    resetKey,
    onStateChange,
  }: {
    onSolved: (solution: string) => void;
    onStateChange: (stateChange: string) => void;

    // The captcha is reset whenever the `resetKey` changes and is above 0
    resetKey: number;
  }) => {
    const container = useRef<HTMLDivElement>(null);
    const widget = useRef<WidgetHandle>();

    const [widgetState, setWidgetState] = useState<WidgetState | 'clean'>(
      'clean',
    );

    useEffect(() => {
      onStateChange(widgetState);
    }, [onStateChange, widgetState]);

    const initWidget = async () => {
      const sdk = await generateFriendlyCaptchaInstance();

      if (container.current) {
        widget.current = sdk.createWidget({
          sitekey: 'FCMVSH69L0LBFDIL',
          element: container.current,
        });
      }

      if (widget.current) {
        widget.current.start();
      }
    };

    const start = useCallback(() => {
      initWidget().then(() => {
        if (widget.current && container.current) {
          widget.current.addEventListener(
            'frc:widget.complete',
            (event: FRCWidgetCompleteEvent) => {
              // Set solution
              setWidgetState(event.detail.state);
              onSolved(event.detail.response);
            },
          );

          widget.current.addEventListener(
            'frc:widget.error',
            (event: FRCWidgetWidgetErrorEvent) => {
              setWidgetState(event.detail.state);
              console.error(
                'Captcha widget ran into an error:',
                event.detail.error,
              );
            },
          );

          widget.current.addEventListener(
            'frc:widget.statechange',
            ({ detail }) => {
              if (detail.state === 'unactivated' && widget.current) {
                setWidgetState('clean');
                widget.current.start();
              }
            },
          );
        }
      });
    }, [container, onSolved]);

    useEffect(() => {
      if (container.current) {
        start();
      }
    }, [start]);

    return <div ref={container} />;
  },
);

export default FriendlyCaptcha;
